import { useState } from "react";
import Sidebar from "../components/Sidebar";
import { auth } from "../firebase";
import { API_BASE_URL } from "../config";
import { parseCortevaWorkbook } from "../utils/cortevaWorkbook";
import CortevaManualTemplates from "./CortevaManualTemplates";
import CortevaCrcUpload from "./CortevaCrcUpload";
import CortevaRecurringUpload from "./CortevaRecurringUpload";
import CortevaCropConsultant from "./CortevaCropConsultant";
import CortevaApprovalControls from "../components/CortevaApprovalControls";

const pkr = (amount, decimals = 0) => `PKR ${Number(amount || 0).toLocaleString("en-PK", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;

function detectedPeriod(name) {
  const match = name.match(/(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[-_ ]?(20\d{2}|\d{2})/i);
  return match ? `${match[1].slice(0, 3)}-${match[2].slice(-2)}` : "";
}

const CROP_OPTIONS = [
  { value: "recurring", label: "IFS FO — upload recurring workbook" },
  { value: "cropConsultant", label: "Consultant — fixed monthly fee" }
];
const CORTEVA_OPTIONS = [
  { value: "seeds", label: "Seed DGs — upload workbook" },
  { value: "crc", label: "CRC Staff — upload client workbook" },
  { value: "recurring", label: "Agronomy — upload recurring workbook" },
  { value: "manual", label: "Graphic Designer / CRE / Consultant / Dispatch Rider — no upload" }
];

export default function CortevaInvoice() {
  const isCrop = new URLSearchParams(window.location.search).get("client") === "CORTEVA_CROP";
  const options = isCrop ? CROP_OPTIONS : CORTEVA_OPTIONS;
  const [mode, setMode] = useState(isCrop ? "recurring" : "seeds");
  const [fileName, setFileName] = useState("");
  const [parsed, setParsed] = useState(null);
  const [period, setPeriod] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  async function onFile(event) {
    const file = event.target.files?.[0];
    setParsed(null);
    setMessage("");
    setFileName(file?.name || "");
    if (!file) return;
    try {
      const workbook = parseCortevaWorkbook(await file.arrayBuffer());
      setParsed(workbook);
      setPeriod(detectedPeriod(file.name));
      setMessage(`${workbook.headCount} employee rows reconciled across Combined Salary and the three regional detail tabs.`);
    } catch (error) {
      setMessage(error.message);
    }
  }

  async function download(type) {
    if (!parsed || !invoiceNumber.trim() || !invoiceDate || !period.trim()) {
      setMessage("Upload a valid workbook and enter invoice number, date, and payroll period.");
      return false;
    }
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setMessage("Sign in before generating PDFs.");
      return false;
    }
    const token = await currentUser.getIdToken();
    const response = await fetch(`${API_BASE_URL}/invoices/download`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ invoiceType: type, invoiceNumber: invoiceNumber.trim(), invoiceDate, period: period.trim(), employees: parsed.employees })
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `${type} PDF failed (${response.status}).`);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Corteva-${type === "corteva-summary" ? "GST-Invoice" : "Employee-Detail"}-${invoiceNumber.trim().replace(/[^a-zA-Z0-9_-]/g, "-")}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return true;
  }

  async function generateBoth() {
    setBusy(true);
    try {
      const first = await download("corteva-summary");
      if (first) {
        await download("corteva-detail");
        setMessage("Both Corteva PDFs downloaded. Review and approve them before sending to the client.");
      }
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  return <div className="app-shell"><Sidebar /><main className="main-content">
    <header className="page-header"><div><div className="eyebrow">{isCrop ? "Corteva Crop invoice workflow" : "Corteva invoice workflow"}</div><h1 className="page-title">{isCrop ? "Corteva Crop Pakistan (Private) Limited" : "Corteva Invoice Builder"}</h1><p className="page-copy">{isCrop ? "IFS FO and the fixed-fee Consultant invoice bill under Corteva Crop's own NTN — separate from Corteva Agriscience." : "Choose Seed DGs workbook intake or a no-upload segment template. GST applies to service charges only."}</p></div><button className="btn btn-muted" type="button" onClick={() => { window.history.pushState({}, "", "/corteva-reconciliation"); window.dispatchEvent(new PopStateEvent("popstate")); }}>Corteva Reconciliation</button></header>
    <section className="panel" style={{ marginBottom: 24 }}><div className="panel-body"><div className="field"><label>Invoice workflow</label><select className="input" value={mode} onChange={(event) => setMode(event.target.value)}>{options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></div></div></section>
    {mode === "crc" ? <CortevaCrcUpload /> : mode === "recurring" ? <CortevaRecurringUpload /> : mode === "manual" ? <CortevaManualTemplates initialSegment="graphic" /> : mode === "cropConsultant" ? <CortevaCropConsultant /> : <>
    <section className="panel"><div className="panel-header"><div><h2 className="panel-title">Source workbook</h2><p className="page-copy">Requires Combined Salary, Lahore, Multan (2), and Sahiwal (2) tabs. Employee codes must match across them.</p></div></div><div className="panel-body">
      <div className="field"><label>Choose Corteva Seed DGs workbook (.xlsx)</label><input className="input" type="file" accept=".xlsx" onChange={onFile} /></div>
      {fileName && <p className="page-copy">{fileName}</p>}
      {parsed && <div className="summary-grid" style={{ marginTop: 20 }}><div className="stat-card"><span>Employees</span><strong>{parsed.headCount}</strong></div><div className="stat-card"><span>Salary and benefits reimbursement</span><strong>{pkr(parsed.reimbursement, 2)}</strong></div><div className="stat-card"><span>Service charges</span><strong>{pkr(parsed.serviceCharges)}</strong></div><div className="stat-card"><span>GST 16% on service charges</span><strong>{pkr(parsed.gst)}</strong></div><div className="stat-card"><span>Gross invoice</span><strong>{pkr(parsed.total, 2)}</strong></div></div>}
    </div></section>
    <section className="panel" style={{ marginTop: 24 }}><div className="panel-header"><div><h2 className="panel-title">Invoice details</h2><p className="page-copy">Enter the number for this invoice; generating PDFs does not reserve or approve it.</p></div></div><div className="panel-body"><div className="form-grid">
      <div className="field"><label>Invoice number *</label><input className="input" value={invoiceNumber} onChange={(event) => setInvoiceNumber(event.target.value)} placeholder="e.g. 27-009/Cor/ALM" /></div>
      <div className="field"><label>Invoice date *</label><input className="input" type="date" value={invoiceDate} onChange={(event) => setInvoiceDate(event.target.value)} /></div>
      <div className="field"><label>Payroll month *</label><input className="input" value={period} onChange={(event) => setPeriod(event.target.value)} placeholder="Aug-26" /></div>
    </div><button className="button button-primary" type="button" disabled={busy || !parsed} onClick={generateBoth}>{busy ? "Generating PDFs…" : "Generate Both PDFs"}</button>{parsed && <CortevaApprovalControls invoice={{ invoiceNumber, invoiceDate, period, segmentKey: "seeds", segmentName: "Seed DGs", headCount: parsed.headCount, reimbursement: parsed.reimbursement, reimbursementBreakdown: parsed.reimbursementBreakdown, serviceCharges: parsed.serviceCharges, invoiceAmount: Number(parsed.reimbursement || 0) + Number(parsed.serviceCharges || 0), gstAmount: parsed.gst, grossAmount: parsed.total }} />}{message && <p className="page-copy" role="status" style={{ marginTop: 12 }}>{message}</p>}</div></section></>}
  </main></div>;
}
